import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ZoomableImage from '../shared/ZoomableImage';
import { copyToClipboard } from '../../utils/clipboard';

export type PreviewType = 'pdf' | 'image' | 'text' | null;

export interface FilePreviewModalProps {
  open: boolean;
  src: string | null;
  title: string;
  type: PreviewType;
  textContent?: string;
  textTruncated?: boolean;
  textPreviewLimitLabel?: string;
  // Extracted text (OCR / native) for the previewed file. When `ocrAvailable`
  // is true a second "Texto extraído" tab is shown alongside the original.
  ocrText?: string | null;
  ocrAvailable?: boolean;
  ocrLoading?: boolean;
  onClose: () => void;
}

function FilePreviewModal({
  open,
  src,
  title,
  type,
  textContent = '',
  textTruncated = false,
  textPreviewLimitLabel = '5000',
  ocrText = null,
  ocrAvailable = false,
  ocrLoading = false,
  onClose,
}: FilePreviewModalProps) {
  const hasOriginal = type === 'text' || !!src;
  // Text-only formats (DOCX, XLSX, RTF, CSV, TXT, MD, JSON, …) have no visual
  // "original": the original tab would just repeat the backend-extracted text
  // already shown under "Texto extraído". So we collapse them to a single
  // extracted-text view. Only PDF and image keep the dual original/extracted
  // tabs, since their original is a genuine visual rendering.
  const isTextOnly = type === 'text';
  const [activeTab, setActiveTab] = useState<'original' | 'ocr'>('original');
  const [copied, setCopied] = useState(false);

  // Reset the active tab whenever a new file is opened. Text-only files always
  // land on the extracted-text view.
  useEffect(() => {
    if (open) {
      setActiveTab((hasOriginal && !isTextOnly) ? 'original' : 'ocr');
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, title]);

  if (!open) return null;
  if (!hasOriginal && !ocrAvailable) return null;

  const ocrHasText = !!(ocrText && ocrText.trim());
  // For text-only files the extracted text is the single source of truth: prefer
  // the full OCR text, falling back to the inline text content while OCR loads.
  const extractedText = (ocrHasText ? (ocrText as string) : (textContent || ''));
  const extractedHasText = !!(extractedText && extractedText.trim());
  // Dual tabs only for genuine visual originals (PDF / image).
  const showTabs = ocrAvailable && hasOriginal && !isTextOnly;
  const viewingExtracted = isTextOnly || activeTab === 'ocr';

  const handleCopyOcr = () => {
    if (!extractedText) return;
    copyToClipboard(extractedText).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderOriginal = () => (
    type === 'pdf' && src ? (
      <PdfContainer>
        <PdfFrame
          src={src}
          title={`Pré-visualização de ${title}`}
        />
        <PdfFallbackMessage>
          Seu navegador deve mostrar o PDF acima. Se preferir,{' '}
          <PdfFallbackLink href={src} target="_blank" rel="noopener noreferrer">
            abra em uma nova aba
          </PdfFallbackLink>.
        </PdfFallbackMessage>
      </PdfContainer>
    ) : type === 'text' ? (
      <PreviewTextContainer>
        <PreviewTextContent>{textContent}</PreviewTextContent>
        {textTruncated && (
          <PreviewTextTruncatedNote>
            Prévia limitada aos primeiros {textPreviewLimitLabel} caracteres — o arquivo completo é enviado ao modelo.
          </PreviewTextTruncatedNote>
        )}
      </PreviewTextContainer>
    ) : src ? (
      <ImageZoomContainer>
        <ZoomableImage src={src} alt={title} />
      </ImageZoomContainer>
    ) : null
  );

  const renderOcr = () => (
    <PreviewTextContainer>
      {ocrLoading && !extractedHasText ? (
        <OcrStateNote>Carregando texto extraído…</OcrStateNote>
      ) : extractedHasText ? (
        <PreviewTextContent>{extractedText}</PreviewTextContent>
      ) : (
        <OcrStateNote>Nenhum texto foi extraído deste arquivo.</OcrStateNote>
      )}
    </PreviewTextContainer>
  );

  return (
    <PreviewOverlay onClick={onClose}>
      <PreviewDialog onClick={(e) => e.stopPropagation()}>
        <PreviewHeader>
          <PreviewTitle title={title}>{title}</PreviewTitle>
          <PreviewHeaderActions>
            {viewingExtracted && extractedHasText && (
              <OcrCopyButton onClick={handleCopyOcr} aria-label="Copiar texto extraído">
                {copied ? <CheckIcon style={{ fontSize: 14 }} /> : <ContentCopyIcon style={{ fontSize: 14 }} />}
                {copied ? 'Copiado' : 'Copiar'}
              </OcrCopyButton>
            )}
            <ClosePreviewButton onClick={onClose} aria-label="Fechar prévia">
              <CloseIcon />
            </ClosePreviewButton>
          </PreviewHeaderActions>
        </PreviewHeader>
        {showTabs ? (
          <PreviewTabsBar>
            <PreviewTab $active={activeTab === 'original'} onClick={() => setActiveTab('original')}>
              Arquivo original
            </PreviewTab>
            <PreviewTab $active={activeTab === 'ocr'} onClick={() => setActiveTab('ocr')}>
              Texto extraído
            </PreviewTab>
          </PreviewTabsBar>
        ) : isTextOnly && extractedHasText ? (
          // Text-only formats: a single, non-clickable "Texto extraído"
          // label (the original would just duplicate this text).
          <PreviewTabsBar>
            <PreviewTab as="div" $active={true} style={{ cursor: 'default' }}>
              Texto extraído
            </PreviewTab>
          </PreviewTabsBar>
        ) : null}
        <PreviewBody>
          {viewingExtracted ? renderOcr() : renderOriginal()}
        </PreviewBody>
      </PreviewDialog>
    </PreviewOverlay>
  );
}

export default FilePreviewModal;

// ============ Styled Components ============

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.preview_overlay_bg};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const PreviewDialog = styled.div`
  font-family: var(--font-secondary);
  background: ${({ theme }) => theme.preview_dialog_bg};
  color: ${({ theme }) => theme.font_color};
  border-radius: 10px;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
  margin: 10px;

  @media (max-width: 600px) {
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    margin: 10px;
    border-radius: 8px;
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.preview_header_border};
  flex-shrink: 0;
  min-height: 50px;

  @media (max-width: 600px) {
    padding: 8px 10px;
    min-height: 44px;
  }
`;

const PreviewTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  max-width: 70vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 0.85rem;
    max-width: calc(100vw - 80px);
  }
`;

const ClosePreviewButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  @media (max-width: 600px) {
    min-width: 48px;
    min-height: 48px;
    padding: 12px;
  }
`;

const PreviewHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const OcrCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.preview_header_border};
  color: inherit;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.75rem;
  padding: 4px 8px;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const PreviewTabsBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.preview_header_border};
  flex-shrink: 0;
`;

const PreviewTab = styled.button<{ $active: boolean }>`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.blue_color : 'transparent')};
  color: inherit;
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 10px;

  &:hover {
    opacity: 1;
  }
`;

const OcrStateNote = styled.div`
  margin: auto;
  padding: 24px;
  text-align: center;
  opacity: 0.7;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color};
`;

const PreviewBody = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 8px;
  }
`;

const ImageZoomContainer = styled.div`
  /* Imagem usa praticamente todo o diálogo (95vw/95vh menos header e
     paddings): área maior = mais pixels para o zoom, sem cap em px. */
  width: calc(95vw - 44px);
  height: calc(95vh - 92px);

  @media (max-width: 600px) {
    width: calc(100vw - 40px);
    height: calc(95vh - 150px);
  }
`;

const PreviewTextContainer = styled.div`
  /* Match the PDF container so the modal keeps the same size across tabs. */
  width: min(90vw, 960px);
  height: min(85vh, 900px);
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 600px) {
    width: calc(100vw - 40px);
    height: calc(95vh - 150px);
  }
`;

const PreviewTextContent = styled.pre`
  margin: 0;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.preview_text_bg};
  color: ${({ theme }) => theme.font_color};
  flex: 1;
  min-height: 0;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Fira Code', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const PreviewTextTruncatedNote = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.7;
`;

const PdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: min(90vw, 960px);
  height: min(85vh, 900px);

  @media (max-width: 600px) {
    width: calc(100vw - 40px);
    height: calc(95vh - 150px);
    gap: 8px;
  }
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.input_surface_bg};
  box-shadow: 0 2px 16px ${({ theme }) => theme.preview_shadow};
`;

const PdfFallbackMessage = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color};
`;

const PdfFallbackLink = styled.a`
  color: ${({ theme }) => theme.blue_color};
  text-decoration: underline;
`;
